/**
 * ModelLoader - Loads and manages the GLTF 3D model
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

export class ModelLoader {
  constructor(baseUrl = '') {
    this.loader = new GLTFLoader();
    this.baseUrl = baseUrl;
  }

  /**
   * Load the GLTF model
   * @param {string} path - Path to GLTF file (can be absolute or relative)
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<THREE.Group>}
   */
  load(path, onProgress = null) {
    // Use absolute path if provided, otherwise prepend baseUrl
    const fullPath = path.startsWith('http') ? path : this.baseUrl + path;
    
    console.log(`📦 Loading model from: ${fullPath}`);
    
    return new Promise((resolve, reject) => {
      this.loader.load(
        fullPath,
        (gltf) => {
          console.log('✅ Model loaded successfully');
          
          const model = gltf.scene;
          
          // Calculate bounds
          const box = new THREE.Box3().setFromObject(model);
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());
          
          console.log('Model info:', {
            bounds: { min: box.min, max: box.max },
            center: center,
            size: size
          });
          
          // Ensure all materials are properly loaded with textures
          model.traverse((child) => {
            if (child.isMesh) {
              // Store original material for later use
              child.userData.originalMaterial = child.material.clone();
              
              // Ensure material renders properly
              if (child.material) {
                child.material.needsUpdate = true;
                
                // If it's a MeshStandardMaterial or similar, ensure it responds to light
                if (child.material.isMeshStandardMaterial || child.material.isMeshPhongMaterial) {
                  // Material is fine
                } else if (child.material.isMeshBasicMaterial) {
                  // Convert basic material to standard for better rendering
                  const oldMaterial = child.material;
                  child.material = new THREE.MeshStandardMaterial({
                    map: oldMaterial.map,
                    color: oldMaterial.color,
                    side: oldMaterial.side
                  });
                  child.userData.originalMaterial = child.material;
                }
              }
            }
          });
          
          // Realsee uses Z-up coordinate system, Three.js uses Y-up
          // Rotate model -90° on X-axis to convert Z-up to Y-up (flipped to correct orientation)
          model.rotation.x = -Math.PI / 2; // -90 degrees
          
          // After rotation, recalculate bounds
          model.updateMatrixWorld(true);
          const rotatedBox = new THREE.Box3().setFromObject(model);
          const rotatedCenter = rotatedBox.getCenter(new THREE.Vector3());
          
          // Center the model horizontally (X and Z)
          model.position.x = -rotatedCenter.x;
          model.position.z = -rotatedCenter.z;
          
          // Position model so its bottom is at floor level (Y=0)
          model.position.y = -rotatedBox.min.y;
          
          console.log('✅ Model rotated from Z-up to Y-up and repositioned:', {
            rotation: { x: 90, y: 0, z: 0 },
            position: model.position,
            originalBounds: { min: box.min, max: box.max },
            rotatedBounds: { min: rotatedBox.min, max: rotatedBox.max }
          });
          
          resolve(model);
        },
        (progress) => {
          if (onProgress) {
            const percent = (progress.loaded / progress.total) * 100;
            onProgress(percent);
          }
        },
        (error) => {
          console.error('❌ Error loading model:', error);
          reject(error);
        }
      );
    });
  }

  /**
   * Make model invisible but keep for raycasting
   * @param {THREE.Group} model
   */
  makeInvisible(model) {
    model.traverse((child) => {
      if (child.isMesh) {
        child.material = new THREE.MeshBasicMaterial({
          visible: false,
          colorWrite: false,
          depthWrite: false,
          depthTest: true
        });
        child.userData.isGeometry = true;
      }
    });
  }

  /**
   * Make model visible with standard materials
   * @param {THREE.Group} model
   */
  makeVisible(model) {
    model.traverse((child) => {
      if (child.isMesh) {
        // Restore or create basic material
        if (!child.userData.originalMaterial) {
          child.material = new THREE.MeshStandardMaterial({
            color: 0xcccccc,
            roughness: 0.7,
            metalness: 0.3
          });
        } else {
          child.material = child.userData.originalMaterial;
        }
        child.material.visible = true;
      }
    });
  }

  /**
   * Get all mesh objects for raycasting
   * @param {THREE.Group} model
   * @returns {Array<THREE.Mesh>}
   */
  getMeshes(model) {
    const meshes = [];
    model.traverse((child) => {
      if (child.isMesh) {
        meshes.push(child);
      }
    });
    return meshes;
  }
}

